import type { ReactNode } from 'react';
import { Box, Link, Typography } from '@mui/material';
import { parseMarkdown, type Inline } from '../../../lib/safeMarkdown';

/**
 * Renders assistant markdown as React elements. Never uses innerHTML: see
 * lib/safeMarkdown.ts for why a model answer cannot inject markup here.
 */
function renderInline(nodes: Inline[]): ReactNode[] {
  return nodes.map((n, i) => {
    switch (n.t) {
      case 'text':
        return n.v;
      case 'br':
        return <br key={i} />;
      case 'bold':
        return <strong key={i}>{renderInline(n.c)}</strong>;
      case 'italic':
        return <em key={i}>{renderInline(n.c)}</em>;
      case 'code':
        return (
          <Box key={i} component="code" sx={{ fontFamily: 'monospace', bgcolor: 'action.hover', px: 0.5, borderRadius: 0.5 }}>
            {n.v}
          </Box>
        );
      case 'link':
        return (
          <Link key={i} href={n.href} target="_blank" rel="noopener noreferrer">
            {renderInline(n.c)}
          </Link>
        );
    }
  });
}

export default function SafeMarkdown({ text }: { text: string }) {
  return (
    <Box sx={{ wordBreak: 'break-word', '& > :last-child': { mb: 0 } }}>
      {parseMarkdown(text).map((block, i) => {
        switch (block.t) {
          case 'heading':
            return (
              <Typography key={i} variant="subtitle2" fontWeight={700} sx={{ mb: 0.5 }}>
                {renderInline(block.c)}
              </Typography>
            );
          case 'list':
            return (
              <Box key={i} component={block.ordered ? 'ol' : 'ul'} sx={{ m: 0, mb: 1, pl: 3 }}>
                {block.items.map((item, j) => (
                  <li key={j}>
                    <Typography variant="body2" component="span">{renderInline(item)}</Typography>
                  </li>
                ))}
              </Box>
            );
          case 'code':
            return (
              <Box key={i} component="pre" sx={{ m: 0, mb: 1, p: 1, overflowX: 'auto', bgcolor: 'action.hover', borderRadius: 1, fontSize: 13 }}>
                {block.v}
              </Box>
            );
          default:
            return (
              <Typography key={i} variant="body2" sx={{ mb: 1 }}>
                {renderInline(block.c)}
              </Typography>
            );
        }
      })}
    </Box>
  );
}
